import { useEffect, useState } from 'react';
import { App, Button, Card, Space, Tag, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { scopes } from '../../../../authConfig';
import { getNoeticaAutodialConfig, putNoeticaAutodialConfig } from '../../../../services/admin-page-service';
import type { NoeticaAutodialAdminConfig } from '../../../../dto/admin-config-models';
import { strings } from '../../../../lang';

const { Title, Text } = Typography;

const linesToText = (lines: string[] | undefined | null) =>
  (Array.isArray(lines) ? lines : []).map((s) => (s ?? '').trim()).filter((s) => s.length > 0).join('\n');

const textToLines = (text: string) =>
  text
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

/** E.164-style: optional leading +, then digits only (no spaces, parentheses, or dashes). */
const phoneLinePattern = /^\+?[0-9]+$/;

const findInvalidCliLine = (lines: string[]): { line: number; value: string } | null => {
  for (let i = 0; i < lines.length; i++) {
    if (!phoneLinePattern.test(lines[i])) {
      return { line: i + 1, value: lines[i] };
    }
  }
  return null;
};

/** Blocked list: digits, +, -, (, ) only (no spaces or letters). */
const blockedLinePattern = /^[0-9+()\-]+$/;

const findInvalidBlockedLine = (lines: string[]): { line: number; value: string } | null => {
  for (let i = 0; i < lines.length; i++) {
    if (!blockedLinePattern.test(lines[i])) {
      return { line: i + 1, value: lines[i] };
    }
  }
  return null;
};

const AdminAutodialConfig = () => {
  const labels = strings.autodialerSettings.phoneNumberSettings;
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState<Pick<NoeticaAutodialAdminConfig, 'lastModifiedUtc' | 'modifiedBy'> | null>(null);
  const [cliText, setCliText] = useState('');
  const [blockedText, setBlockedText] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const data = await getNoeticaAutodialConfig(token.accessToken);
        if (data) {
          const cliJoined = linesToText(data.cliRotationNumbers);
          const blockedJoined = linesToText(data.blockedPhoneNumbers);
          setCliText(cliJoined);
          setBlockedText(blockedJoined);
          setMeta({ lastModifiedUtc: data.lastModifiedUtc, modifiedBy: data.modifiedBy });
          if (cliJoined.length === 0) {
            setLoadError(
              labels.emptyCliList,
            );
          }
        } else {
          setLoadError(labels.loadError);
          notification.error({ message: labels.loadFailed });
        }
      } catch (e) {
        setLoadError(String(e));
        notification.error({ message: labels.loadFailed, description: String(e) });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [instance, inProgress, notification]);

  const save = async () => {
    const cliRotationNumbers = textToLines(cliText);
    const blockedPhoneNumbers = textToLines(blockedText);

    const invalidCli = findInvalidCliLine(cliRotationNumbers);
    if (invalidCli) {
      notification.error({
        message: labels.invalidCallerNumber,
        description: labels.invalidCallerNumberDescription(invalidCli.line, invalidCli.value),
      });
      return;
    }

    const invalidBlocked = findInvalidBlockedLine(blockedPhoneNumbers);
    if (invalidBlocked) {
      notification.error({
        message: labels.invalidBlocked,
        description: labels.invalidBlockedDescription(invalidBlocked.line, invalidBlocked.value),
      });
      return;
    }

    try {
      setSaving(true);
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const ok = await putNoeticaAutodialConfig(token.accessToken, { cliRotationNumbers, blockedPhoneNumbers });
      if (!ok) {
        notification.error({ message: labels.saveFailed, description: labels.serverError });
        return;
      }
      notification.success({ message: labels.saved });
      const data = await getNoeticaAutodialConfig(token.accessToken);
      if (data) {
        setCliText(linesToText(data.cliRotationNumbers));
        setBlockedText(linesToText(data.blockedPhoneNumbers));
        setMeta({ lastModifiedUtc: data.lastModifiedUtc, modifiedBy: data.modifiedBy });
      }
    } catch (e) {
      notification.error({ message: labels.saveFailed, description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const cliLines = textToLines(cliText);
  const blockedLines = textToLines(blockedText);

  return (
    <div style={{ padding: 0, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ width: '100%' }}>
          <Title level={4} style={{ margin: '0 0 4px' }}>{labels.title}</Title>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <Text type="secondary">{labels.description}</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, flexWrap: 'wrap' }}>
              {meta && (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {labels.lastUpdated} {new Date(meta.lastModifiedUtc).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}{meta.modifiedBy ? ` ${labels.modifiedBy} ${meta.modifiedBy}` : ''}
                </Text>
              )}
              <Button type="primary" onClick={save} loading={saving} disabled={loading} style={{ whiteSpace: 'nowrap' }}>
                {labels.save}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {loadError && (
        <div style={{ marginBottom: 8 }}>
          <Text type="danger">{loadError}</Text>
        </div>
      )}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          title={labels.callerNumbers}
          loading={loading}
          extra={<Tag>{labels.entries(cliLines.length)}</Tag>}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {labels.callerNumbersDescription}
          </Text>
          <TextArea
            rows={8}
            value={cliText}
            onChange={(e) => setCliText(e.target.value)}
            placeholder={labels.placeholder}
            style={{ fontFamily: 'monospace' }}
          />
        </Card>
        <Card
          title={labels.blockedNumbers}
          loading={loading}
          extra={<Tag>{labels.entries(blockedLines.length)}</Tag>}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {labels.blockedNumbersDescription}
          </Text>
          <TextArea
            rows={12}
            value={blockedText}
            onChange={(e) => setBlockedText(e.target.value)}
            placeholder={labels.placeholder}
            style={{ fontFamily: 'monospace' }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default AdminAutodialConfig;
